import { createContext, useContext, useMemo } from 'react';

import PropTypes from 'prop-types';

import {
  ACTIVATION_MODE,
  CHANGE_PASSWORD_MODE,
  RENEW_PASSWORD_MODE,
  SIGN_IN_EMAIL_PASSWORD_MODE,
  VERIFYING_MODE
} from '../config';
import SignOutSubscriber from '../components/SignOutSubscriber';
import UserSignInForm from '../components/UserSignInForm';
import ChangePasswordForm from '../components/ChangePasswordForm';
import ResetPasswordForm from '../components/ResetPasswordForm';
import AmplifySubscriber from '../components/AmplifySubscriber';

const AccessContext = createContext({});

export function AuthContextProvider(
  {
    amplifyConfig = {},
    newUserChecker = () => false,
    paths: {
      signIn: signInPathname = import.meta.env.NX_SIGN_IN_EMAIL_PASSWORD_PATH || '/sign-in',
      signOut: signOutPathname = import.meta.env.NX_SIGN_OUT_PATH || '/sign-out',
      activate: activatePathname = import.meta.env.NX_ACTIVATE_IN_EMAIL_PASSWORD_PATH || '/activate',
      changePassword: changePasswordPathname = import.meta.env.NX_CHANGE_PASSWORD_PATH || '/change-password',
      renewPassword: renewPasswordPathname = import.meta.env.NX_RENEW_PASSWORD_PATH || '/reset-password',
      signInRedirect: signInRedirectPathname = import.meta.env.NX_SIGN_IN_DEFAULT_REDIRECT_PATH || '/',
      signOutRedirect: signOutRedirectPathname = import.meta.env.NX_SIGN_OUT_DEFAULT_REDIRECT_PATH || '/sign-in',
      verificationPath: verifyingRedirectPathname = import.meta.env.NX_SIGN_OUT_DEFAULT_REDIRECT_PATH || '/sign-in/verifying',
    } = {},
    enableModes = [
      SIGN_IN_EMAIL_PASSWORD_MODE,
      RENEW_PASSWORD_MODE,
      ACTIVATION_MODE,
      CHANGE_PASSWORD_MODE,
      VERIFYING_MODE
    ],
    enableAmplify = true,
    SignInForm,
    ChangePasswordForm: ExternalPasswordForm,
    ResetPasswordForm: ExternalResetPasswordForm,
    isExternalAuth = import.meta.env.NX_SIGN_IN_EXTERNAL === 'true',
    children,
  }
) {
  const value = useMemo(() => ({
    amplifyConfig,
    newUserChecker,
    signInPathname,
    signOutPathname,
    activatePathname,
    changePasswordPathname,
    renewPasswordPathname,
    signInRedirectPathname,
    signOutRedirectPathname,
    verifyingRedirectPathname,
    enableModes,
    isExternalAuth,
    enableAmplify,
    SignInForm: SignInForm || UserSignInForm,
    ChangePasswordForm: ExternalPasswordForm || ChangePasswordForm,
    ResetPasswordForm: ExternalResetPasswordForm || ResetPasswordForm,
  }), [
    amplifyConfig,
    newUserChecker,
    signInPathname,
    signOutPathname,
    activatePathname,
    changePasswordPathname,
    renewPasswordPathname,
    signInRedirectPathname,
    signOutRedirectPathname,
    verifyingRedirectPathname,
    enableModes,
    isExternalAuth,
    enableAmplify,
    SignInForm,
    ExternalPasswordForm,
    ExternalResetPasswordForm
  ]);
  return (
    <AccessContext.Provider value={value}>
      <SignOutSubscriber />
      {enableAmplify && <AmplifySubscriber />}
      {children}
    </AccessContext.Provider>
  );
}

export const useAuthContext = () => useContext(AccessContext);

AuthContextProvider.propTypes = {
  amplifyConfig: PropTypes.any,
  newUserChecker: PropTypes.func,
  paths: PropTypes.shape({
    signIn: PropTypes.string,
    changePassword: PropTypes.string,
    signInRedirect: PropTypes.string,
    signOutRedirect: PropTypes.string,
    verificationPath: PropTypes.string,
  }),
  enableModes: PropTypes.array,
  enableAmplify: PropTypes.bool,
  SignInForm: PropTypes.elementType,
  ChangePasswordForm: PropTypes.elementType,
  ResetPasswordForm: PropTypes.elementType,
  isExternalAuth: PropTypes.bool,
  children: PropTypes.node.isRequired,
};

export default AuthContextProvider;
